
import { AfterViewInit, Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { VisualizationType } from '../../enums/widget.enum';
import { ChartData, ChartSchema, DynamicChart, WidgetData } from '../../models/widget.models';
import { SubscriptionHandlerDirective } from '../../directives/subscription-handler.directive';

@Component({
    selector: 'app-chat-widget',
    templateUrl: './chat-widget.component.html',
    styleUrls: ['./chat-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatWidgetComponent extends SubscriptionHandlerDirective implements OnInit, AfterViewInit {
    @Input() widgetData: WidgetData;
    @Input() isMobileApp: boolean;
    plotlyWidget: any;

    constructor() {
        super();
    }

    colorPalette: string[] = [
        '#E65F70', '#E88D56', '#E5B537', '#26A799', '#8DC34F',
        '#867864', '#4696E5', '#9146DC', '#BE5FCD', '#556270',
        '#767F89', '#A3A3A3', '#79C2DB', '#9F8DD0', '#EBA4BE',
        '#E5E5BE', '#86D1C3', '#E4BECB', '#B7D699', '#71CDE0',
        '#EBBD79', '#C094DA', '#A3D8EC', '#E7AAC3', '#BAD9BB',
        '#C7BCB4', '#EBD0A5', '#D3AFDB', '#ACD1EC', '#CDE0BA',
        '#EBD179', '#A3D1CB', '#E0E4B4', '#C3B6DE', '#B8BDDD',
        '#EBBCAC', '#D8DF90', '#A3DEE4', '#EBBBBC', '#BAD9BB'
    ];

    loader: boolean = false;
    ngOnInit(): void {
        if (this.widgetData) {
            this.loader = true;
            if (this.widgetData.visualizationType == VisualizationType.DYNAMIC_CHART) {
                // Chart config is passed to the UI with the relevant data. Today we use plotly.
                const dynamicChart = this.widgetData.data as DynamicChart;
                const chartSchema: ChartSchema = dynamicChart.config;
                const chartData = dynamicChart.data;
                this.configureDynamicWidget(chartSchema, chartData);
            }
            else {
                console.error(`Unsupported inline visualization: ${this.widgetData}`)
            }
            this.loader = false;
        }
    }

    ngAfterViewInit(): void {
    }

    private mapChartData(chartData: ChartData, chartSchema: ChartSchema): any {
        return Object.keys(chartData.series).map((dataKey: string) => {
            return {
                x: chartData.x,
                y: chartData.series[dataKey],
                name: dataKey,
                type: chartSchema.chartType.toLowerCase()
            }
        })
    }

    /**
     * Create a plotly widget form the chart schema and data
     * @param chartSchema 
     * @param chartData x: number[], y: number[][]
     */
    configureDynamicWidget(chartSchema: ChartSchema, chartData: ChartData) {
        let data = this.mapChartData(chartData, chartSchema);
        this.plotlyWidget = {
            data: data,
            layout: {
                title: chartSchema.title,
                font: {
                    family: 'Lato, sans-serif',
                    size: 10,
                    color: '#7f7f7f'
                },
                colorway: this.colorPalette,
                xaxis: {
                    title: {
                        text: chartSchema.x?.label
                    }
                },
                yaxis: {
                    title: {
                        text: chartSchema.y?.label
                    }
                },
            },
            config: {
                displayModeBar: false,
                responsive: false
            }
        }

        if (chartSchema.y.dataType?.toLowerCase() == 'numeric') {
            this.plotlyWidget.layout.yaxis.range = this.getAxisRange(chartData.series);
        }

        if (this.isMobileApp) {
            this.plotlyWidget.layout.width = window.innerWidth - 75;
            this.plotlyWidget.layout.height = 300;
        }
    }

    /**
     * Adjust the axis range by +/- 10% of the range
     * @param chartData 
     */
    private getAxisRange(seriesData: { [key: string]: number[] }): number[] {
        let allValues: number[] = [];
        
        // Combine all values from all series into a single array
        Object.values(seriesData).forEach(series => {
            allValues = allValues.concat(series);
        });
    
        if (allValues.length === 0) {
            return [0, 0];
        }
    
        let min = Math.min(...allValues);
        let max = Math.max(...allValues);
        let range = max - min;
    
        let extension = 0.1 * range;
    
        let extendedMin = Math.floor(min >= 0 && min <= extension ? 0.0 : min - extension);
        let extendedMax = Math.ceil(max + extension);
        return [extendedMin, extendedMax];
    }
}
